/**
 * Guest User Management Utilities
 * Handles guest user identification and cart management for guest checkout
 */

import { v4 as uuidv4 } from 'uuid';

// LocalStorage keys
const GUEST_ID_KEY = 'quickcrate_guest_id';
const GUEST_CART_KEY = 'quickcrate_guest_cart';
const GUEST_SESSION_KEY = 'quickcrate_guest_session';

/**
 * Get or create a guest ID
 * @returns {string} Guest user ID (UUID)
 */
export const getOrCreateGuestId = () => {
  let guestId = localStorage.getItem(GUEST_ID_KEY);
  
  if (!guestId) {
    guestId = uuidv4();
    localStorage.setItem(GUEST_ID_KEY, guestId);
  }
  
  return guestId;
};

/**
 * Check if user is logged in
 * @returns {boolean} True if user has valid token and userID
 */
export const isUserLoggedIn = () => {
  const token = localStorage.getItem('token');
  const userID = localStorage.getItem('userID');
  return !!(token && userID);
};

/**
 * Get current user ID (logged in user or guest)
 * @returns {string} User ID or Guest ID
 */
export const getCurrentUserId = () => {
  if (isUserLoggedIn()) {
    return localStorage.getItem('userID');
  }
  return getOrCreateGuestId();
};

/**
 * Get guest cart from localStorage
 * @returns {Array} Array of cart items
 */
export const getGuestCart = () => {
  try {
    const cart = localStorage.getItem(GUEST_CART_KEY);
    return cart ? JSON.parse(cart) : [];
  } catch (error) {
    console.error('Error loading guest cart:', error);
    return [];
  }
};

/**
 * Save guest cart to localStorage
 * @param {Array} cart - Array of cart items
 */
export const saveGuestCart = (cart) => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Error saving guest cart:', error);
  }
};

/**
 * Add item to guest cart
 * @param {Object} item - Cart item to add
 * @returns {Array} Updated cart
 */
export const addToGuestCart = (item) => {
  const cart = getGuestCart();
  
  // Check if item already exists
  const existingIndex = cart.findIndex(cartItem => cartItem.productID === item.productID);
  
  if (existingIndex !== -1) {
    // Update quantity
    cart[existingIndex].quantity += item.quantity;
  } else {
    // Add new item
    cart.push({
      ...item,
      addedAt: new Date().toISOString(),
      guestId: getOrCreateGuestId()
    });
  }
  
  saveGuestCart(cart);
  return cart;
};

/**
 * Update guest cart item quantity
 * @param {string} productID - Product ID
 * @param {number} quantity - New quantity
 * @returns {Array} Updated cart
 */
export const updateGuestCartItem = (productID, quantity) => {
  const cart = getGuestCart();
  const itemIndex = cart.findIndex(item => item.productID === productID);
  
  if (itemIndex !== -1) {
    if (quantity <= 0) {
      cart.splice(itemIndex, 1);
    } else {
      cart[itemIndex].quantity = quantity;
    }
  }
  
  saveGuestCart(cart);
  return cart;
};

/**
 * Remove item from guest cart
 * @param {string} productID - Product ID to remove
 * @returns {Array} Updated cart
 */
export const removeFromGuestCart = (productID) => {
  const cart = getGuestCart().filter(item => item.productID !== productID);
  saveGuestCart(cart);
  return cart;
};

/**
 * Clear guest cart
 */
export const clearGuestCart = () => {
  localStorage.removeItem(GUEST_CART_KEY);
};

/**
 * Get guest cart count
 * @returns {number} Total items in cart
 */
export const getGuestCartCount = () => {
  const cart = getGuestCart();
  return cart.reduce((total, item) => total + item.quantity, 0);
};

/**
 * Create guest session for checkout
 * @param {Object} sessionData - Session data
 */
export const createGuestSession = (sessionData) => {
  try {
    localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify({
      ...sessionData,
      guestId: getOrCreateGuestId(),
      createdAt: new Date().toISOString()
    }));
  } catch (error) {
    console.error('Error creating guest session:', error);
  }
};

/**
 * Get guest session
 * @returns {Object|null} Session data or null
 */
export const getGuestSession = () => {
  try {
    const session = localStorage.getItem(GUEST_SESSION_KEY);
    return session ? JSON.parse(session) : null;
  } catch (error) {
    console.error('Error loading guest session:', error);
    return null;
  }
};

/**
 * Clear guest session
 */
export const clearGuestSession = () => {
  localStorage.removeItem(GUEST_SESSION_KEY);
};

/**
 * Migrate guest cart to user account after login
 * @param {string} userID - User ID after login
 * @returns {Array} Guest cart items to sync
 */
export const migrateGuestCartToUser = (userID) => {
  const guestCart = getGuestCart();
  
  if (guestCart.length > 0) {
    // Return cart items for backend sync
    const itemsToMigrate = guestCart.map(item => ({
      ...item,
      applicationUserId: userID,
      migratedFromGuest: true
    }));
    
    // Clear guest cart after migration
    clearGuestCart();
    
    return itemsToMigrate;
  }
  
  return [];
};

/**
 * Clear all guest data
 */
export const clearAllGuestData = () => {
  clearGuestCart();
  clearGuestSession();
  // Note: We keep guest ID for potential future tracking
};
